import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Channel } from "../lib/api";
import { useSession } from "../session";
import {
  Badge,
  Card,
  Empty,
  ErrorBox,
  Loading,
  Notice,
  PageHeader,
  SegmentedControl,
} from "../components/ui";
import { IconBroadcast } from "../components/icons";
import { ago, dateTime, num, stateWord } from "../lib/format";

interface Available {
  tokel: {
    available: boolean;
    error?: string;
    channels: {
      id: number;
      slug: string;
      name: string;
      platform: string;
      chat_id: number | null;
      is_active: boolean;
      archived: boolean;
      feed_posts: number;
      feed_from: string | null;
      feed_to: string | null;
    }[];
  };
  platforms: string[];
}

export default function ChannelsPage() {
  const { channels, can, refresh } = useSession();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<"tokel" | "platform" | "demo">("demo");

  const available = useQuery({
    queryKey: ["available"],
    queryFn: () => api.get<Available>("/channels/available"),
    enabled: can("manage_channels"),
  });

  const connect = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<{ channel: Channel }>("/channels", body),
    onSuccess: () => {
      queryClient.invalidateQueries();
      refresh();
    },
  });

  return (
    <>
      <PageHeader
        title="Каналы"
        lede="Канал можно взять из операционного Tokel (тогда видны и внутренности конвейера), подключить напрямую с площадки или посмотреть демонстрационный — он работает без единого ключа."
      />

      <Card title="Подключённые">
        {channels.length === 0 ? (
          <Empty title="Пока пусто" icon={<IconBroadcast size={22} />}>
            Подключите первый канал ниже.
          </Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Канал</th>
                  <th>Площадка</th>
                  <th>Источник</th>
                  <th className="num">Подписчиков</th>
                  <th>Добор</th>
                  <th>Сбор</th>
                </tr>
              </thead>
              <tbody>
                {channels.map((channel) => (
                  <tr key={channel.id}>
                    <td>
                      {channel.title}
                      <div className="faint">{channel.slug}</div>
                    </td>
                    <td>{channel.platform}</td>
                    <td className="dim">{channel.source_kind}</td>
                    <td className="num mono">{num(channel.subscribers)}</td>
                    <td>
                      <Badge
                        tone={
                          channel.backfill_status === "done"
                            ? "ok"
                            : channel.backfill_status === "failed"
                              ? "alert"
                              : "muted"
                        }
                      >
                        {stateWord(channel.backfill_status)}
                      </Badge>
                      {channel.backfill_status === "done" && (
                        <div className="faint">{num(channel.backfill_posts)} публикаций</div>
                      )}
                      {channel.backfill_error && <div className="faint">{channel.backfill_error}</div>}
                    </td>
                    <td className="dim" title={dateTime(channel.last_ingest_at)}>
                      {ago(channel.last_ingest_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {can("manage_channels") && (
        <Card title="Подключить">
          <SegmentedControl
            value={mode}
            onChange={(value) => setMode(value)}
            ariaLabel="Откуда подключаем"
            options={[
              { value: "demo", label: "Демонстрационный" },
              { value: "tokel", label: "Из Tokel" },
              { value: "platform", label: "С площадки" },
            ]}
          />

          {connect.error && <ErrorBox error={connect.error} />}
          {connect.isSuccess && (
            <Notice>Канал подключён. Первичный добор истории идёт в фоне — это минуты.</Notice>
          )}

          {mode === "demo" && (
            <div className="stack">
              <p className="muted">
                Синтетический канал с историей за несколько месяцев. Нужен, чтобы пройти
                все экраны без ключей Telegram, MAX и базы Tokel. В отчётах он помечен
                как демонстрационный и с настоящими каналами не смешивается.
              </p>
              <button
                className="primary"
                disabled={connect.isPending}
                onClick={() => connect.mutate({ source_kind: "demo", title: "Демо-канал", platform: "demo" })}
              >
                Создать демо-канал
              </button>
            </div>
          )}

          {mode === "tokel" && (
            <TokelList available={available.data} loading={available.isLoading} onConnect={(id, name) =>
              connect.mutate({ source_kind: "tokel", tokel_channel_id: id, title: name })
            } />
          )}

          {mode === "platform" && <PlatformForm onConnect={(body) => connect.mutate(body)} busy={connect.isPending} />}
        </Card>
      )}
    </>
  );
}

function TokelList({
  available,
  loading,
  onConnect,
}: {
  available?: Available;
  loading: boolean;
  onConnect: (id: number, name: string) => void;
}) {
  if (loading) return <Loading rows={3} />;
  if (!available?.tokel.available) {
    return (
      <p className="muted">
        База Tokel не подключена{available?.tokel.error ? `: ${available.tokel.error}` : ""}. Задайте
        TCA_TOKEL_DATABASE_URL — пользователю в той базе достаточно права SELECT.
      </p>
    );
  }
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Канал</th>
            <th>Площадка</th>
            <th className="num">Лента</th>
            <th>Период</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {available.tokel.channels.map((channel) => (
            <tr key={channel.id}>
              <td>
                {channel.name}
                <div className="faint">{channel.slug}</div>
              </td>
              <td>{channel.platform}</td>
              <td className="num mono">{num(channel.feed_posts)}</td>
              <td className="dim">
                {channel.feed_from ? `${channel.feed_from.slice(0, 10)} — ${channel.feed_to?.slice(0, 10)}` : "—"}
              </td>
              <td>
                <button
                  className="small"
                  disabled={channel.feed_posts === 0}
                  title={channel.feed_posts === 0 ? "По этому каналу истории ещё нет" : undefined}
                  onClick={() => onConnect(channel.id, channel.name)}
                >
                  Подключить
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PlatformForm({
  onConnect,
  busy,
}: {
  onConnect: (body: Record<string, unknown>) => void;
  busy: boolean;
}) {
  const [platform, setPlatform] = useState("telegram");
  const [identifier, setIdentifier] = useState("");
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onConnect({ source_kind: "platform", platform, identifier });
      }}
    >
      <div className="grid two">
        <div className="field">
          <label>Площадка</label>
          <select value={platform} onChange={(event) => setPlatform(event.target.value)}>
            <option value="telegram">Telegram</option>
            <option value="max">MAX</option>
          </select>
        </div>
        <div className="field">
          <label>Адрес канала</label>
          <input
            required
            placeholder="@name или ссылка"
            value={identifier}
            onChange={(event) => setIdentifier(event.target.value)}
          />
        </div>
      </div>
      <p className="faint" style={{ marginBottom: "var(--s3)" }}>
        Для Telegram нужна настроенная сессия MTProto, для MAX — токен аккаунта. Проверить связь
        можно в настройках.
      </p>
      <button className="primary" type="submit" disabled={busy}>
        Подключить
      </button>
    </form>
  );
}
